import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isUndefined from 'lodash/isUndefined';
import { ProfileContext } from 'context';
import { isManualReading } from 'helpers/externalDataSources';
import { getStandards } from 'helpers/standards';
import MetricConversions from 'libs/MetricConversions';
import { isAggregatedPostMeal } from 'libs/StatsCalculations';
import Account from 'modules/Account';
import App from 'modules/App';
import { CGM_INTERVAL } from 'modules/PatientResults/constants';
import * as selectors from 'modules/PatientResults/selectors';
import { getIsPlainValuesMode } from '../helpers';
const cgmInterval = CGM_INTERVAL * 60;
const prepareTrendData = (dataType, readings, averageData, tickValues, standards, metricsUnits, isPlainValuesMode, isInProgress)=>{
    const metricConversions = new MetricConversions(metricsUnits);
    const conversion = metricConversions.bloodGlucoseConcentration;
    const { unitSymbol } = conversion;
    const lowPreMealThreshold = conversion.toDisplay(standards.preMeal.lowThreshold);
    const highPreMealThreshold = conversion.toDisplay(standards.preMeal.highThreshold);
    const lowPostMealThreshold = conversion.toDisplay(standards.postMeal.lowThreshold);
    const highPostMealThreshold = conversion.toDisplay(standards.postMeal.highThreshold);
    const all = [];
    const high = [];
    const target = [];
    const low = [];
    const highReadings = [];
    const targetReadings = [];
    const lowReadings = [];
    const jointReadings = [];
    const averageAll = [];
    const averageHigh = [];
    const averageTarget = [];
    const averageLow = [];
    const stdDevs = {};
    const originalTimestamps = {};
    const flags = {};
    const manualReadings = [];
    let maxReadingValue = 0;
    let minReadingValue = 0;
    let oldestReadingTimestamp = 0;
    let newestReadingTimestamp = 0;
    readings.forEach((reading, idx)=>{
        const x = reading.timestamp;
        if (!oldestReadingTimestamp || oldestReadingTimestamp > x) {
            oldestReadingTimestamp = x;
        }
        if (!newestReadingTimestamp || newestReadingTimestamp < x) {
            newestReadingTimestamp = x;
        }
        const y = isPlainValuesMode ? reading.value : conversion.toDisplay(reading.value);
        if (y > maxReadingValue) {
            maxReadingValue = y;
        }
        if (!minReadingValue || y < minReadingValue) {
            minReadingValue = y;
        }
        const highConverted = isAggregatedPostMeal(reading.flags) ? highPostMealThreshold : highPreMealThreshold;
        const lowConverted = isAggregatedPostMeal(reading.flags) ? lowPostMealThreshold : lowPreMealThreshold;
        all.push({
            x,
            y
        });
        if (!isPlainValuesMode) {
            let nextX;
            let nextY;
            const nextReading = dataType === 'CGM' ? readings[idx + 1] : null;
            if (nextReading) {
                nextX = nextReading.timestamp;
                nextY = conversion.toDisplay(nextReading.value);
            }
            if (y > highConverted) {
                high.push({
                    x,
                    y
                });
                target.push({
                    x,
                    y: null
                });
                low.push({
                    x,
                    y: null
                });
                highReadings.push(x);
                if (nextReading) {
                    if (nextY < lowConverted) {
                        const diff = Math.round((nextX - x) / 3);
                        high.push({
                            x: x + diff,
                            y: highConverted
                        });
                        target.push({
                            x: x + diff,
                            y: highConverted
                        });
                        target.push({
                            x: nextX - diff,
                            y: lowConverted
                        });
                        low.push({
                            x: nextX - diff,
                            y: lowConverted
                        });
                        jointReadings.push(x + diff);
                        jointReadings.push(nextX - diff);
                    } else if (nextY <= highConverted) {
                        const diff = Math.round((nextX - x) / 2);
                        high.push({
                            x: x + diff,
                            y: highConverted
                        });
                        target.push({
                            x: x + diff,
                            y: highConverted
                        });
                        jointReadings.push(x + diff);
                    }
                }
            } else if (y < lowConverted) {
                high.push({
                    x,
                    y: null
                });
                target.push({
                    x,
                    y: null
                });
                low.push({
                    x,
                    y
                });
                lowReadings.push(x);
                if (nextReading) {
                    if (nextY > highConverted) {
                        const diff = Math.round((nextX - x) / 3);
                        low.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        target.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        target.push({
                            x: nextX - diff,
                            y: highConverted
                        });
                        high.push({
                            x: nextX - diff,
                            y: highConverted
                        });
                        jointReadings.push(x + diff);
                        jointReadings.push(nextX - diff);
                    } else if (nextY >= lowConverted) {
                        const diff = Math.round((nextX - x) / 2);
                        low.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        target.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        jointReadings.push(x + diff);
                    }
                }
            } else {
                high.push({
                    x,
                    y: null
                });
                target.push({
                    x,
                    y
                });
                low.push({
                    x,
                    y: null
                });
                targetReadings.push(x);
                if (nextReading) {
                    if (nextY < lowConverted) {
                        const diff = Math.round((nextX - x) / 2);
                        low.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        target.push({
                            x: x + diff,
                            y: lowConverted
                        });
                        jointReadings.push(x + diff);
                    } else if (nextY > highConverted) {
                        const diff = Math.round((nextX - x) / 2);
                        high.push({
                            x: x + diff,
                            y: highConverted
                        });
                        target.push({
                            x: x + diff,
                            y: highConverted
                        });
                        jointReadings.push(x + diff);
                    }
                }
            }
            if (nextReading) {
                const xDiff = nextX - x;
                if (xDiff > cgmInterval) {
                    const breakX = x + xDiff / 2;
                    high.push({
                        x: breakX,
                        y: null
                    });
                    target.push({
                        x: breakX,
                        y: null
                    });
                    low.push({
                        x: breakX,
                        y: null
                    });
                }
            }
        }
        if (!isUndefined(reading.stdDev)) {
            stdDevs[x] = isPlainValuesMode ? reading.stdDev : conversion.toDisplay(reading.stdDev);
        }
        if (reading.originalTimestamp) {
            originalTimestamps[x] = reading.originalTimestamp;
        }
        flags[x] = reading.flags;
        if (isManualReading(reading)) {
            manualReadings.push(x);
        }
    });
    if (averageData) {
        averageData.forEach((averageReading)=>{
            const x = averageReading.timestamp;
            const y = isPlainValuesMode ? averageReading.value : conversion.toDisplay(averageReading.value);
            if (y > maxReadingValue) {
                maxReadingValue = y;
            }
            averageAll.push({
                x,
                y
            });
            const highConverted = Math.max(highPostMealThreshold, highPreMealThreshold);
            const lowConverted = Math.min(lowPostMealThreshold, lowPreMealThreshold);
            if (!isPlainValuesMode) {
                if (y > highConverted) {
                    averageHigh.push({
                        x,
                        y
                    });
                } else if (y < lowConverted) {
                    averageLow.push({
                        x,
                        y
                    });
                } else {
                    averageTarget.push({
                        x,
                        y
                    });
                }
            }
        });
    }
    if (isInProgress) {
        const preLoaderTimestamps = [];
        const postLoaderTimestamps = [];
        for(let i = 1; i <= tickValues.length; i += 2){
            const placeholderValue = 100 + 100 / (i * 2);
            if (tickValues[i] < oldestReadingTimestamp) {
                preLoaderTimestamps.push({
                    x: tickValues[i],
                    y: conversion.toDisplay(placeholderValue)
                });
            } else if (tickValues[i] > newestReadingTimestamp) {
                postLoaderTimestamps.push({
                    x: tickValues[i],
                    y: conversion.toDisplay(placeholderValue)
                });
            }
        }
        if (preLoaderTimestamps.length) {
            all.unshift(...preLoaderTimestamps);
        }
        if (postLoaderTimestamps.length) {
            all.push(...postLoaderTimestamps);
        }
    }
    return {
        points: [
            {
                id: 'high',
                data: high
            },
            {
                id: 'target',
                data: target
            },
            {
                id: 'low',
                data: low
            },
            {
                id: 'all',
                data: all
            },
            {
                id: 'averageHigh',
                data: averageHigh
            },
            {
                id: 'averageTarget',
                data: averageTarget
            },
            {
                id: 'averageLow',
                data: averageLow
            },
            {
                id: 'averageAll',
                data: averageAll
            }
        ],
        levelTimestamps: {
            highReadings,
            targetReadings,
            lowReadings,
            jointReadings
        },
        manualReadings,
        flags,
        originalTimestamps,
        standardDeviations: stdDevs,
        maxReadingValue,
        minReadingValue,
        oldestReadingTimestamp,
        newestReadingTimestamp,
        unitSymbol
    };
};
export const useGlucoseTrendData = (dataType, readings, averageData, tickValues, isInProgress)=>{
    const profileContext = useContext(ProfileContext);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const clinicSettings = useSelector(profileContext.selectors.clinicSettings);
    const calculationFormula = useSelector(selectors.calculationFormula);
    const countrySettings = useSelector(App.selectors.countrySettings);
    const metricsUnits = useSelector(Account.selectors.metricsUnits);
    const [glucoseConcentrationTrendData, setGlucoseConcentrationTrendData] = useState({
        points: null,
        levelTimestamps: null,
        manualReadings: [],
        flags: {},
        originalTimestamps: {},
        standardDeviations: {},
        maxReadingValue: 0,
        minReadingValue: 0,
        oldestReadingTimestamp: 0,
        newestReadingTimestamp: 0,
        unitSymbol: null
    });
    const isPlainValuesMode = getIsPlainValuesMode(calculationFormula);
    useEffect(()=>{
        const standards = getStandards(phiSet, countrySettings, clinicSettings);
        const trendData = prepareTrendData(dataType, readings, averageData, tickValues, standards, metricsUnits, isPlainValuesMode, isInProgress);
        setGlucoseConcentrationTrendData(trendData);
    }, [
        readings,
        averageData,
        tickValues,
        phiSet,
        isPlainValuesMode,
        isInProgress
    ]);
    return glucoseConcentrationTrendData;
};
